import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import EventDeskTabLayout from '../EventDeskTabLayout';
import { useStatsig } from '../../../components/hooks/useStatsig';
import { EventDeskTabKeys } from '../../../utils/eventDeskConstants';
import VirtualDrawsList from './VirtualDrawsList';
import DayPlannerHeader from './DayPlannerHeader';
import DayConfigSidePanel from './DayConfigSidePanel';
import DayPlannerCalendar from './DayPlannerCalendar';
import AddDivisionsSidePanel from './AddDivisionsSidePanel';
import SummarySidePanel from './SummarySidePanel';

import * as drawEditActions from '../../../actions/draw-edit-actions';
import { getDayPlannerSpecificData, setPreviousDay, toggleAddDivisionsSidePanel, toggleDayConfigSidePanel, toggleSummarySidePanel } from '../../../actions/day-planner-actions';
import { apiDateFormat, primaryViews } from '../../../utils/dayPlanner';

import styles from './dayPlanner.scss';

const DayPlanner = (props) => {
    const {
        setEventDeskActiveView,
    } = props;

    const history = useHistory();
    const dispatch = useDispatch();
    const { checkGate, featureGates } = useStatsig();
    const isDayPlannerEnabled = checkGate(featureGates.EVENT_DESK_DAY_PLANNER);

    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);
    const {
        event,
        draws,
    } = useSelector(({ drawEdit }) => {
        const { event, draws } = drawEdit;
        return {
            event: event?.data,
            draws,
        };
    });

    const onChangeDate = (day) => {
        if (dayPlanner.dayConfig && dayPlanner.viewState?.day) {
            dispatch(setPreviousDay(dayPlanner.viewState?.day));
        }
        dispatch(getDayPlannerSpecificData(event.id, dayPlanner.viewState?.primaryView, day, null, day));
    };

    const closeDayConfigSidePanel = () => {
        dispatch(toggleDayConfigSidePanel());
    };

    const onClickAddDivisions = () => {
        dispatch(toggleAddDivisionsSidePanel());
    };

    const onCloseSummarySidePanel = () => {
        dispatch(toggleSummarySidePanel());
    };

    const setPrimaryViewCapacitySimulator = () => {
        const { primaryView, day, facilityId } = dayPlanner?.viewState;
        if (primaryView != primaryViews.CapacitySimulator) {
            dispatch(getDayPlannerSpecificData(event.id, primaryViews.CapacitySimulator, day, facilityId));
        }
    };

    const setPrimaryViewCreatedDraws = () => {
        const { primaryView, day, facilityId } = dayPlanner?.viewState;
        if (primaryView != primaryViews.DraftDraws) {
            dispatch(getDayPlannerSpecificData(event.id, primaryViews.DraftDraws, day, facilityId));
        }
    };

    useEffect(() => {
        if (!isDayPlannerEnabled) {
            // load first draw
            dispatch(drawEditActions.getDraw({ eventId: event.id, drawId: draws.data?.[0]?.id, setAsCurrentDraw: true }));
            history.replace({
                search: `?v=${EventDeskTabKeys.DRAW_EDITOR}&d=${draws.data?.[0]?.id}`,
            });
            setEventDeskActiveView(EventDeskTabKeys.DRAW_EDITOR);
        }
    }, []);

    useEffect(() => {
        if (event) {
            const { id, eventSchedule } = event;

            const currentDate = moment.tz(eventSchedule.timeZone?.value).startOf('day');
            const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule.timeZone?.value).startOf('day');
            const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value).startOf('day');

            const initialDate = currentDate.isBefore(eventStartDate) || currentDate.isAfter(eventEndDate)
                    ? eventStartDate
                    : currentDate;

            const dayString = initialDate.format(apiDateFormat);

            dispatch(getDayPlannerSpecificData(id, primaryViews.CapacitySimulator, null, null, dayString));
        }
    }, [event]);

    return (
        <EventDeskTabLayout
            noPadding
            fitScreen={true}
        >
            <div className={`${styles.dayPlannerToggleBar}`}>
                <div className={styles.displayType}>
                    <button
                        type="button"
                        className={`btn btn-sm ${styles.primaryViewButton} ${dayPlanner.viewState?.primaryView === primaryViews.CapacitySimulator ? styles.selected : ''}`}
                        onClick={setPrimaryViewCapacitySimulator}
                        data-testid="day-planner.capacity-simulator-btn"
                    >
                        Capacity Simulator
                    </button>
                    &nbsp;
                    <button
                        type="button"
                        className={`btn btn-sm ${styles.primaryViewButton} ${dayPlanner.viewState?.primaryView === primaryViews.DraftDraws ? styles.selected : ''}`}
                        onClick={setPrimaryViewCreatedDraws}
                        data-testid="day-planner.created-draws-btn"
                    >
                        Created Draws
                    </button>
                </div>
            </div>
            <div className={`${styles.dayPlanner} d-flex`}>
                <VirtualDrawsList
                    className={styles.sidebar}
                    defaultDrawStartMode={dayPlanner?.eventPlan?.drawStartMode}
                    onClickAddDivisions={onClickAddDivisions}
                />
                <div className={`${styles.courtGrid} flex-1 mw0`} data-testid="day-planner.court-grid">
                    {dayPlanner?.viewState?.day && (
                        <DayPlannerHeader
                            eventSchedule={event?.eventSchedule}
                            onChangeDate={onChangeDate}
                        />
                    )}
                    {dayPlanner?.viewState?.day && dayPlanner?.dayConfig && (
                        <DayPlannerCalendar
                            event={event}
                            data-testid="day-planner.calendar"
                        />
                    )}
                </div>
            </div>
            {dayPlanner.showDayConfigModal && (
                <DayConfigSidePanel
                    eventId={event?.id}
                    eventSchedule={event?.eventSchedule}
                    closeDayConfigSidePanel={closeDayConfigSidePanel}
                />
            )}
            {dayPlanner.showAddDivisionsSidePanel && (
                <AddDivisionsSidePanel
                    className={styles.addDivisionsSidePanel}
                    eventId={event?.id}
                    eventAllottedMatchTimes={event?.matchTimes}
                    sportTypeId={event?.sportTypeId}
                    eventTypeId={event?.eventTypeId}
                    utrTypeId={event?.utrTypeId}
                    eventSchedule={event?.eventSchedule}
                    closeSidePanel={onClickAddDivisions}
                />
            )}
            {dayPlanner.showSummarySidePanel && (
                <SummarySidePanel
                    className={styles.summarySidePanel}
                    eventId={event?.id}
                    eventSchedule={event?.eventSchedule}
                    closeSidePanel={onCloseSummarySidePanel}
                />
            )}
        </EventDeskTabLayout>
    );
};

DayPlanner.propTypes = {
    setEventDeskActiveView: PropTypes.func.isRequired,
};

DayPlanner.defaultProps = {};

export default DayPlanner;
